import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Save, Plus, X, FileCheck2, ShieldCheck, ClipboardCheck, ScrollText, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CURRENCY_OPTIONS } from '../lib/currency';
import { SectionTitle } from '../components/common';
import { ApprovalChainEditor } from '../components/ApprovalChainEditor';
import { NumberStepper } from '../components/NumberStepper';

const MARKUP_FIELDS = [
  ['escalationPct', 'Escalation %'], ['contingencyPct', 'Contingency %'],
  ['adminOhPct', 'Admin Overheads %'], ['profitPct', 'Profit %'], ['negotiationPct', 'Negotiation %'],
];

export const MARGIN_BASIS_OPTIONS = [
  { value: 'profit', label: 'Profit ÷ Quotation price', short: 'Profit ÷ Quotation' },
  { value: 'profit_negotiation', label: '(Profit + Negotiation) ÷ Quotation price', short: '(Profit + Negotiation) ÷ Quotation' },
  { value: 'gross', label: '(Quotation − Direct Cost) ÷ Quotation price', short: '(Quotation − Direct Cost) ÷ Quotation' },
];

function EditableList({ icon: Icon, label, items, onChange, canEdit, placeholder, testid }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.some((i) => i.toLowerCase() === v.toLowerCase())) { setDraft(''); return; }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2" data-testid={testid}>
      <Label className="flex items-center gap-2"><Icon size={14} className="text-[hsl(var(--brand))]" />{label}</Label>
      <div className="space-y-1.5">
        {items.length === 0 && <div className="rounded-lg border border-dashed px-3 py-2 text-xs text-muted-foreground">No items yet.</div>}
        {items.map((it, i) => (
          <div key={`${it}-${i}`} data-testid={`${testid}-item-${i}`} className="flex items-center justify-between gap-2 rounded-lg border px-3 py-1.5 text-sm">
            <span className="min-w-0 truncate">{it}</span>
            {canEdit && (
              <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => remove(i)}
                className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-[hsl(var(--danger)/0.1)] hover:text-[hsl(var(--danger))]">
                <X size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="flex items-center gap-2">
          <Input value={draft} placeholder={placeholder} data-testid={`${testid}-input`}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${testid}-add`}><Plus size={14} className="mr-1" />Add</Button>
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { user, can } = useAuth();
  const canEdit = can('settings', 'readwrite');
  const isAdmin = user?.groupName === 'Admin';
  const navigate = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => { api.get('/settings').then((r) => setS(r.data)); }, []);
  if (!s) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const dm = s.defaultMarkup || {};
  const setMarkup = (k, v) => setS({ ...s, defaultMarkup: { ...dm, [k]: v } });

  const save = async () => {
    const payload = {
      ...s,
      vatPct: parseFloat(s.vatPct) || 0,
      marginTargetPct: parseFloat(s.marginTargetPct) || 0,
      marginBasis: s.marginBasis || 'profit',
      defaultMarkup: Object.fromEntries(MARKUP_FIELDS.map(([k]) => [k, parseFloat(dm[k]) || 0])),
    };
    try { const { data } = await api.put('/settings', payload); setS(data); toast.success('Settings saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const vatPctNum = (parseFloat(s.vatPct) || 0) * 100;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 md:p-6 lg:p-8">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold md:text-3xl">Settings</h1>
          <p className="text-sm text-muted-foreground">Company details, default markup policy and tax.</p>
        </div>
        {canEdit && <Button data-testid="settings-save" onClick={save}><Save size={15} className="mr-2" />Save</Button>}
      </div>

      {isAdmin && (
        <button type="button" data-testid="settings-audit-link" onClick={() => navigate('/audit-trail')}
          className="erak-card flex w-full items-center gap-4 p-5 text-left transition-colors hover:bg-muted/30">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))]"><ScrollText size={20} /></span>
          <span className="min-w-0 flex-1">
            <span className="block font-display text-base font-semibold">Audit Trail</span>
            <span className="block text-sm text-muted-foreground">Organisation-wide log of costing/project activity, access &amp; config changes and rate-master edits.</span>
          </span>
          <ChevronRight size={18} className="shrink-0 text-muted-foreground" />
        </button>
      )}

      <div className="erak-card space-y-4 p-5">
        <SectionTitle>Company</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Company name</Label><Input value={s.companyName || ''} disabled={!canEdit} onChange={(e) => setS({ ...s, companyName: e.target.value })} /></div>
          <div className="space-y-1.5">
            <Label>Default currency</Label>
            <Select value={(s.currency || 'AED').toUpperCase()} disabled={!canEdit} onValueChange={(v) => setS({ ...s, currency: v })}>
              <SelectTrigger data-testid="settings-currency-select" className="font-num"><SelectValue placeholder="Select currency" /></SelectTrigger>
              <SelectContent>
                {CURRENCY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-muted-foreground">Applied to new tenders &amp; dashboard totals. No FX conversion.</div>
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Address</Label><Input value={s.address || ''} disabled={!canEdit} onChange={(e) => setS({ ...s, address: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>TRN</Label><Input value={s.trn || ''} disabled={!canEdit} onChange={(e) => setS({ ...s, trn: e.target.value })} className="font-num" /></div>
        </div>
      </div>

      <div className="erak-card space-y-4 p-5">
        <SectionTitle>Default Markup Policy</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-3">
          {MARKUP_FIELDS.map(([k, lbl]) => (
            <div key={k} className="space-y-1.5">
              <Label>{lbl}</Label>
              <Input type="number" step="0.01" disabled={!canEdit} className="font-num"
                value={dm[k] ?? 0} onChange={(e) => setMarkup(k, e.target.value)} />
              <div className="text-[10px] text-muted-foreground">{((parseFloat(dm[k]) || 0) * 100).toFixed(1)}%</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Stored as decimals (0.03 = 3%). Applied to new tenders by default; editable per tender.</p>
      </div>

      <div className="erak-card space-y-4 p-5">
        <SectionTitle>Tax &amp; Guardrails</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>VAT %</Label>
            <NumberStepper value={Number.isFinite(vatPctNum) ? +vatPctNum.toFixed(4) : 0} step={0.5} decimals={2} min={0} max={100} suffix="%" size="md" disabled={!canEdit}
              className="w-full" data-testid="settings-vat-input"
              onChange={(v) => setS({ ...s, vatPct: v / 100 })} />
            <div className="text-[10px] text-muted-foreground">Resolved: {vatPctNum.toFixed(2)}% &nbsp;·&nbsp; stored as {(parseFloat(s.vatPct) || 0)} (decimal)</div>
          </div>
          <div className="space-y-1.5">
            <Label>Margin target %</Label>
            <NumberStepper value={Number(s.marginTargetPct ?? 15)} step={0.5} decimals={2} min={0} max={100} suffix="%" size="md" disabled={!canEdit}
              className="w-full" data-testid="settings-margin-target-input"
              onChange={(v) => setS({ ...s, marginTargetPct: v })} />
            <div className="text-[10px] text-muted-foreground">Resolved: {(parseFloat(s.marginTargetPct) || 0).toFixed(2)}% — compared against the margin gauge</div>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Margin basis</Label>
            <Select value={s.marginBasis || 'profit'} disabled={!canEdit} onValueChange={(v) => setS({ ...s, marginBasis: v })}>
              <SelectTrigger data-testid="settings-margin-basis-select"><SelectValue placeholder="Select basis" /></SelectTrigger>
              <SelectContent>
                {MARGIN_BASIS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-muted-foreground">
              Drives the margin gauge and the “below target” guardrail. Net Profit (shown separately) is always Profit after negotiation.
            </div>
          </div>
        </div>
      </div>

      <div className="erak-card space-y-5 p-5" data-testid="settings-cc-config">
        <SectionTitle>Cost Centre Opening — Configurable Lists</SectionTitle>
        <p className="text-xs text-muted-foreground">
          These lists drive the Cost Centre Opening form in the Costing module. Add or remove items without a rebuild.
        </p>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <EditableList
            icon={FileCheck2} label="Document Checklist items" testid="cc-checklist-config"
            items={s.ccChecklistItems || []} canEdit={canEdit}
            onChange={(items) => setS({ ...s, ccChecklistItems: items })}
            placeholder="e.g. Manpower Schedule" />
          <div className="space-y-2" data-testid="cc-enforcement-config">
            <Label className="flex items-center gap-2"><ShieldCheck size={14} className="text-[hsl(var(--brand))]" />Invoice Plan 100% enforcement</Label>
            <Select value={s.invoicePlanEnforcement || 'WARN'} disabled={!canEdit}
              onValueChange={(v) => setS({ ...s, invoicePlanEnforcement: v })}>
              <SelectTrigger data-testid="cc-enforcement-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="WARN">Warn (flag but allow opening)</SelectItem>
                <SelectItem value="BLOCK">Block (require exactly 100% to open)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">Controls whether an under/over-allocated invoice plan blocks Cost Centre opening.</p>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">Remember to press Save to persist changes.</p>
      </div>

      <ApprovalChainEditor />

      <ApprovalChainEditor
        endpoint="/cc-opening-workflow"
        title="Cost Centre Opening Workflow"
        description="The ordered approval stages a completed Cost Centre Opening passes through before it can be opened."
        showTenderHops={false}
        icon={ClipboardCheck}
        testid="cc-opening-chain-editor"
        savedMessage="Cost Centre Opening workflow updated"
      />
    </div>
  );
}
